// src/frontend/js/modern/core/account-guard.js

import { STORAGE_KEYS } from '../shared/constants.js';
import { stateCache } from './state-cache.js';
import { deleteDB } from './storage/idb-driver.js';

const STORAGE_PREFIX = 'wlfmc_';

class AccountGuard {
	constructor() {
		this._checked = false;
	}


	async checkAndPurge() {
		if (this._checked) return false;
		this._checked = true;

		const currentUserId = String(window.wlfmc_l10n?.userId || 0);
		const currentSessionId = String(window.wlfmc_l10n?.sessionId || '');

		const stored = this._readStoredIdentity();

		const storedUserId = stored.userId || '';
		const isAccountChanged = storedUserId !== currentUserId;

		if (isAccountChanged) {
			console.log(
				`[AccountGuard] Account changed: "${storedUserId}" → "${currentUserId}". Purging all local data...`
			);
			await this._purgeAll();
		} else if (stored.sessionId !== currentSessionId) {
			console.log('[AccountGuard] Same user, new session. Updating sessionId.');
		}

		this._writeStoredIdentity(currentUserId, currentSessionId);

		return isAccountChanged;
	}


	_readStoredIdentity() {
		try {
			const raw = localStorage.getItem(STORAGE_KEYS.CONFIG);
			if (!raw) return { userId: '', sessionId: '' };
			const parsed = JSON.parse(raw);
			return {
				userId: String(parsed.userId || ''),
				sessionId: String(parsed.sessionId || ''),
			};
		} catch {
			return { userId: '', sessionId: '' };
		}
	}

	_writeStoredIdentity(userId, sessionId) {
		try {
			localStorage.setItem(STORAGE_KEYS.CONFIG, JSON.stringify({
				userId,
				sessionId,
				isLoggedIn: userId !== '0' && userId !== '',
				mergeLists: window.wlfmc_l10n?.merge_lists === 'true',
			}));
		} catch (e) {
			console.warn('[AccountGuard] Failed to write identity:', e);
		}
	}

	async _purgeAll() {
		this._clearLocalStorage();

		this._clearSessionStorage();

		await this._clearIndexedDB();

		stateCache.clear();

		console.log('[AccountGuard] All local data purged.');
	}

	_clearLocalStorage() {
		try {
			const keysToRemove = [];
			for (let i = 0; i < localStorage.length; i++) {
				const key = localStorage.key(i);
				if (key && key.startsWith(STORAGE_PREFIX)) {
					keysToRemove.push(key);
				}
			}
			keysToRemove.forEach(key => localStorage.removeItem(key));
			if (keysToRemove.length > 0) {
				console.log(`[AccountGuard] Removed ${keysToRemove.length} localStorage keys`);
			}
		} catch (e) {
			console.warn('[AccountGuard] localStorage purge failed:', e);
		}
	}

	_clearSessionStorage() {
		try {
			const keysToRemove = [];
			for (let i = 0; i < sessionStorage.length; i++) {
				const key = sessionStorage.key(i);
				if (key && key.startsWith(STORAGE_PREFIX)) {
					keysToRemove.push(key);
				}
			}
			keysToRemove.forEach(key => sessionStorage.removeItem(key));
		} catch (e) {
		}
	}

	async _clearIndexedDB() {
		try {
			await deleteDB();
			console.log('[AccountGuard] IndexedDB deleted');
		} catch (e) {
			console.warn('[AccountGuard] IndexedDB purge failed:', e);
		}
	}

	destroy() {
		this._checked = false;
	}
}

export const accountGuard = new AccountGuard();
export default accountGuard;
